import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DOMAIN_LOCALES, PLAYLIST_STATUS } from 'shared/const';

import { newSelectTranslation } from '../../../utils';

import {
  createPlaylist,
  deletePlaylist,
  sendPlaylistReminder,
  transferPlaylist,
  updatePlaylist,
} from './graphql/mutations';
import { listPlaylists } from './graphql/queries';

export default ({
  useGraphQLMutation,
  domain,
  isGlobalOrganization,
  listOwnedPlaylists,
  locale,
  managerSelected,
  organizationId,
  remindAllEventHandler,
  selectedPlaylist,
  selectPlaylistHandler,
}) => {
  // Dialog open status variables
  const [openPlaylistForm, setOpenPlaylistForm] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openPauseDialog, setOpenPauseDialog] = useState(false);
  const [openPublishDialog, setOpenPublishDialog] = useState(false);
  const [openAddCoursesToPlaylist, setOpenAddCoursesToPlaylist] =
    useState(false);
  const [openSharePlaylistDialog, setOpenSharePlaylistDialog] = useState(false);
  const [openShowTransferOwnership, setOpenShowTransferOwnership] =
    useState(false);
  const [openCopyPlaylistForm, setOpenCopyPlaylistForm] = useState(false);

  // Dialog status
  const [formStatus, setFormStatus] = useState();
  const [submittingPublish, setSubmittingPublish] = useState(false);
  const [errorMessagePublish, setErrorMessagePublish] = useState();
  const [errorDetailPublish, setErrorDetailPublish] = useState();
  const [successPublish, setSuccessPublish] = useState();

  // Dialog state variables
  const [deletePlaylistId, setDeletePlaylistId] = useState();
  const [editPlaylistId, setEditPlaylistId] = useState();
  const [pausePlaylist, setPausePlaylist] = useState();
  const [pausePlaylistId, setPausePlaylistId] = useState();
  const [publishPlaylistId, setPublishPlaylistId] = useState();
  const [reminderStatus, setReminderStatus] = useState({ open: false });
  const [removeContentId, setRemoveContentId] = useState(false);
  const [scrollToSelected, setScrollToSelected] = useState(null);
  const [selectedPlaylistForReminder, setSelectedPlaylistForReminder] =
    useState(null);
  const [shareValues, setShareValues] = useState(null);
  const [transferValues, setTransferValues] = useState(null);

  const { t } = useTranslation();

  // Pause dialog

  const pauseHandler = (id) => {
    setOpenPauseDialog(true);
    setPausePlaylistId(id);
    setPausePlaylist(listOwnedPlaylists.find((playlist) => playlist.id === id));
    setFormStatus(null);
  };

  const onPauseCloseHandler = () => {
    setOpenPauseDialog(false);
    setPausePlaylistId();
    setPausePlaylist();
    setFormStatus(null);
  };

  // Publish dialog

  const publishHandler = (id) => {
    setOpenPublishDialog(true);
    setPublishPlaylistId(id);
    setFormStatus(null);
    setSubmittingPublish(false);
  };

  const onPublishCloseHandler = () => {
    setOpenPublishDialog(false);
    setPublishPlaylistId();
    setFormStatus(null);
    setSubmittingPublish(false);
  };

  const [updatePlaylistMutation] = useGraphQLMutation(updatePlaylist, {
    update: (cache) => {
      cache.evict({
        id: 'ROOT_QUERY',
        fieldName: 'listPlaylists',
      });
    },
    onCompleted: () => {
      setFormStatus({
        success: true,
        message: t('playlist.playlistUpdatedSuccessMessage'),
      });
    },
    onError: ({ message }) => {
      const errorMessage = message
        ? `${t('playlist.playlistUpdatedFailureMessage')}: ${message}`
        : t('playlist.playlistUpdatedFailureMessage');
      setFormStatus({
        success: false,
        message: errorMessage,
      });
    },
  });

  const [updatePlaylistContentMutation] = useGraphQLMutation(updatePlaylist, {
    onCompleted: () => {
      setFormStatus({
        success: true,
        message: t('playlist.playlistContentUpdatedSuccessMessage'),
      });
    },
    onError: ({ message }) => {
      const errorMessage = message
        ? `${t('playlist.playlistUpdatedFailureMessage')}: ${message}`
        : t('playlist.playlistUpdatedFailureMessage');
      setFormStatus({
        success: false,
        message: errorMessage,
      });
    },
  });

  const [publishPlaylistMutation] = useGraphQLMutation(updatePlaylist, {
    update: (cache) => {
      cache.evict({
        id: 'ROOT_QUERY',
        fieldName: 'listPlaylists',
      });
    },
    onCompleted: () => {
      setSuccessPublish(true);
      onPublishCloseHandler();
    },
    onError: ({ graphQLErrors }) => {
      const { errorDetails } = graphQLErrors[0].extensions;

      if (errorDetails?.length) {
        setErrorDetailPublish(
          errorDetails.map((key) => ({
            title: t(`playlist.publishErrors.${key}`),
            description: t(`playlist.publishErrors.${key}Description`),
          }))
        );
      }
      setErrorMessagePublish(t('playlist.publishError'));
      onPublishCloseHandler();
    },
  });

  const [removeContentMutation] = useGraphQLMutation(updatePlaylist, {
    onCompleted: () => {
      setFormStatus({
        success: true,
        message: t('playlist.removeFromPlaylistSuccessMessage'),
      });
    },
    onError: () => {
      setFormStatus({
        success: false,
        message: t('playlist.removeFromPlaylistFailureMessage'),
      });
    },
  });

  const [createPlaylistMutation, createPlaylistResponse] = useGraphQLMutation(
    createPlaylist,
    {
      onCompleted: ({ createPlaylist }) => {
        if (isGlobalOrganization) {
          setScrollToSelected(createPlaylist.id);
        }
        setFormStatus({
          success: true,
          message: t('playlist.playlistCreatedSuccessMessage'),
        });
      },
      onError: () => {
        setFormStatus({
          success: false,
          message: t('playlist.playlistCreatedFailureMessage'),
        });
      },
      update(cache, { data: { createPlaylist } }, { variables }) {
        const { input } = variables;
        const listVariables1 = {
          filter: {
            administrativeView: true,
            status: ['DRAFT', 'PUBLISHED', 'PAUSED', 'TEMPLATE'],
          },
        };
        const listVariables2 = { filter: { ...listVariables1.filter } };

        if (input.organizationId && input.ownerId) {
          listVariables1.filter.organizationId = input.organizationId;
          listVariables2.filter.organizationId = input.organizationId;
          listVariables2.filter.ownerId = input.ownerId;
        } else {
          listVariables1.filter.isGlobal = true;
          listVariables2.filter.isGlobal = true;
          listVariables2.filter.type = input.type;
        }

        const olderData1 = cache.readQuery({
          query: listPlaylists,
          variables: listVariables1,
        });
        const olderData2 = cache.readQuery({
          query: listPlaylists,
          variables: listVariables2,
        });

        if (olderData1) {
          cache.writeQuery({
            query: listPlaylists,
            variables: listVariables1,
            data: {
              listPlaylists: [...olderData1.listPlaylists, createPlaylist],
            },
          });
        }
        if (olderData2) {
          cache.writeQuery({
            query: listPlaylists,
            variables: listVariables2,
            data: {
              listPlaylists: [...olderData2.listPlaylists, createPlaylist],
            },
          });
        }
      },
    }
  );

  const [sendPlaylistReminderMutation, sendPlaylistReminderResponse] =
    useGraphQLMutation(sendPlaylistReminder, {
      onCompleted: () => {
        setReminderStatus({
          open: true,
          type: 'success',
          message: t('playlist.reminderSentSuccessfully'),
        });
        setSelectedPlaylistForReminder(null);
      },
      onError: () => {
        setReminderStatus({
          open: true,
          type: 'error',
          message: t('playlist.reminderSendingFailed'),
        });
        setSelectedPlaylistForReminder(null);
      },
    });

  const onCreatePlaylist = (values) => {
    const input = {
      name: values.name,
      description: values.description,
      languages: [DOMAIN_LOCALES[domain][0]],
      templateId: values.templateId,
    };

    if (!isGlobalOrganization) {
      input.ownerId = managerSelected;
      input.organizationId = organizationId;
    }

    createPlaylistMutation({
      variables: {
        input,
      },
    });
  };

  const updatePlaylistHandler = (values) => {
    const input = {
      id: values.id,
      name: values.name,
      description: values.description,
    };

    updatePlaylistMutation({
      variables: {
        input,
      },
    });
  };

  const onClosePlaylistFormHandler = () => {
    setOpenPlaylistForm(false);
    setEditPlaylistId();
    setFormStatus(null);
  };

  const editPlaylistHandler = (id) => {
    setEditPlaylistId(id);
    setOpenPlaylistForm(true);
  };

  const [deletePlaylistMutation] = useGraphQLMutation(deletePlaylist, {
    update(cache, { data: { deletePlaylist } }) {
      cache.evict({ id: cache.identify(deletePlaylist) });
      cache.gc();
    },
    onCompleted: () => {
      selectPlaylistHandler(null);
      setFormStatus({
        success: true,
        message: t('playlist.deletePlaylistSuccessMessage'),
      });
    },
    onError() {
      setFormStatus({
        success: false,
        message: t('playlist.deletePlaylistFailureMessage'),
      });
    },
  });

  const [transferPlaylistMutation] = useGraphQLMutation(transferPlaylist, {
    update(cache, { data: { transferPlaylist } }, { variables }) {
      const oldOwnerVariables = {
        filter: {
          administrativeView: true,
          organizationId,
          ownerId: transferValues.ownerId,
          status: ['DRAFT', 'PUBLISHED', 'PAUSED', 'TEMPLATE'],
        },
      };
      const oldOwnerData = cache.readQuery({
        query: listPlaylists,
        variables: oldOwnerVariables,
      });

      const newOwnerVariables = {
        filter: {
          administrativeView: true,
          organizationId,
          ownerId: variables.input.newOwnerId,
          status: ['DRAFT', 'PUBLISHED', 'PAUSED', 'TEMPLATE'],
        },
      };
      const newOwnerData = cache.readQuery({
        query: listPlaylists,
        variables: newOwnerVariables,
      });

      if (newOwnerData) {
        cache.writeQuery({
          query: listPlaylists,
          variables: newOwnerVariables,
          data: {
            listPlaylists: [...newOwnerData.listPlaylists, transferPlaylist],
          },
        });
      }

      if (oldOwnerData) {
        cache.writeQuery({
          query: listPlaylists,
          variables: oldOwnerVariables,
          data: {
            listPlaylists: oldOwnerData.listPlaylists.filter(
              (playlist) => playlist.id !== transferPlaylist.id
            ),
          },
        });
      }
    },
    onCompleted: () => {
      setFormStatus({
        success: true,
        message: t('administrateUsers.transferredOwnershipSuccess'),
      });
    },
    onError() {
      setFormStatus({
        success: false,
        message: t('administrateUsers.transferredOwnershipFailure'),
      });
    },
  });

  const deleteHandler = (playlistId) => {
    setRemoveContentId(false);
    setOpenDeleteDialog(true);
    setDeletePlaylistId(playlistId);
  };

  const onDeletePlaylist = (playlistId) => {
    deletePlaylistMutation({
      variables: {
        playlistId,
      },
    });
  };

  const onDeleteCloseHandler = () => {
    setOpenDeleteDialog(false);
    setDeletePlaylistId();
    setFormStatus(null);
  };

  const onStatusUpdate = (id, status) => {
    updatePlaylistMutation({
      variables: {
        input: { id, status },
      },
    });
  };

  const onPublish = () => {
    setSubmittingPublish(true);

    publishPlaylistMutation({
      variables: {
        input: {
          id: publishPlaylistId,
          status: isGlobalOrganization
            ? PLAYLIST_STATUS.TEMPLATE
            : PLAYLIST_STATUS.PUBLISHED,
        },
      },
    });
  };

  // Content dialog

  const addContentHandler = () => {
    setOpenAddCoursesToPlaylist(true);
  };

  const updatePlaylistContentHandler = (playlist, coursesSelected) => {
    const input = {
      id: playlist.id,
      courses: {
        create: coursesSelected
          .filter((course) => !playlist.courses.find((c) => c.id === course.id))
          .map((c) => c.id),
        delete: playlist.courses
          .filter((course) => !coursesSelected.find((c) => c.id === course.id))
          .map((c) => c.id),
      },
    };

    updatePlaylistContentMutation({
      variables: {
        input,
      },
    });
  };

  const deleteFromPlaylist = (contentId) => {
    setDeletePlaylistId(false);
    setOpenDeleteDialog(true);
    setRemoveContentId(contentId);
  };

  const removeFromPlaylistHandler = (contentId) => {
    const input = { id: selectedPlaylist.id, courses: { delete: [contentId] } };
    removeContentMutation({
      variables: {
        input,
      },
    });
  };

  // Transfer dialog

  const onTransferOwnershipHandler = (id) => {
    const newOwnerId = id[0].id;
    const input = {
      playlistId: transferValues.id,
      newOwnerId,
    };

    transferPlaylistMutation({ variables: { input } });
  };

  const openTransferOwnershipHandler = (id) => {
    const playlist = listOwnedPlaylists.find((item) => item.id === id);
    const title = newSelectTranslation(playlist.name, locale);
    setTransferValues({
      id,
      name: title,
      ownerId: playlist.ownerId,
    });
    setOpenShowTransferOwnership(true);
  };

  const onCloseTransferHandler = () => {
    setOpenShowTransferOwnership(false);
    setFormStatus(null);
    setTransferValues(null);
  };

  // Share dialog

  const openSharePlaylistHandler = async (playlistId, playlistTitle) => {
    setOpenSharePlaylistDialog(true);
    const selectedPlaylist = listOwnedPlaylists.find(
      (playlist) => playlist.id === playlistId
    );

    setShareValues({
      playlist: selectedPlaylist,
      playlistName: playlistTitle,
    });
  };

  const closeShareDialogHandler = () => {
    setShareValues(null);
    setOpenSharePlaylistDialog(false);
  };

  // Reminder dialog

  const sendReminderHandler = () => {
    // send matomo event in client
    if (remindAllEventHandler) {
      remindAllEventHandler();
    }

    sendPlaylistReminderMutation({
      variables: {
        playlistId: selectedPlaylistForReminder,
      },
    });
  };

  return {
    // Share open variables
    openPlaylistForm,
    openDeleteDialog,
    openPauseDialog,
    openPublishDialog,
    openAddCoursesToPlaylist,
    openSharePlaylistDialog,
    openShowTransferOwnership,
    openCopyPlaylistForm,
    setOpenAddCoursesToPlaylist,
    setOpenCopyPlaylistForm,
    setOpenPlaylistForm,
    // Dialog status
    formStatus,
    submittingPublish,
    errorMessagePublish,
    errorDetailPublish,
    successPublish,
    setErrorDetailPublish,
    setErrorMessagePublish,
    setFormStatus,
    setSuccessPublish,
    submittingForm: createPlaylistResponse.loading,
    // Dialog state variables
    deletePlaylistId,
    editPlaylistId,
    pausePlaylist,
    pausePlaylistId,
    publishPlaylistId,
    reminderStatus,
    removeContentId,
    scrollToSelected,
    selectedPlaylist,
    selectedPlaylistForReminder,
    setScrollToSelected,
    setSelectedPlaylistForReminder,
    shareValues,
    submittingReminder: sendPlaylistReminderResponse.loading,
    transferValues,
    // Playlists dialogs
    onCreatePlaylist,
    updatePlaylistHandler,
    onClosePlaylistFormHandler,
    editPlaylistHandler,
    deleteHandler,
    onDeletePlaylist,
    onDeleteCloseHandler,
    onStatusUpdate,
    // Pause dialog
    pauseHandler,
    onPauseCloseHandler,
    // Publish dialog
    publishHandler,
    onPublish,
    onPublishCloseHandler,
    // Content dialog
    addContentHandler,
    updatePlaylistContentHandler,
    deleteFromPlaylist,
    removeFromPlaylistHandler,
    // Transfer dialog
    openTransferOwnershipHandler,
    onTransferOwnershipHandler,
    onCloseTransferHandler,
    // Share dialog
    openSharePlaylistHandler,
    closeShareDialogHandler,
    // Reminder dialog
    sendReminderHandler,
    setReminderStatus,
  };
};
